import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, DataBlock, Grid } from '@fs/zion-ui'
import { PersonLink, TreePersonLink, UserLink } from '../../../app/Links'
import { isNullOrUndefined } from '../../../../util/Utils'
import { LabelsBlock } from '../../commonComponents/CommonComponents'
import { DmcGrid } from '../../../common/DmcGrid'
import { DmcText } from '../../../common/DmcText'
import { TreeIdCells } from './TreeIdBlock'

/** 'MALE' and 'Male' are the same answer; rendered as they arrive they read as a disagreement. */
function titleCase(value) {
  if (isNullOrUndefined(value) || value === '') {
    return value
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export function HistoricalMessageSummary({ messageDetails, queueCachesState, queueCachesActions }) {
  const { t } = useTranslation()
  // DATA-12381: HISTORICAL_PERSON_V2 messages carry `personId` for the detected person and describe the
  // curated person in the payload. The old queue uses `matchedPersonId` and describes nobody, so the
  // component fetched both people from Family Tree. Branching rather than forking the component (BR-37).
  const isV2 = !isNullOrUndefined(messageDetails.personId)

  const detectedPersonId = isV2 ? messageDetails.personId : messageDetails.matchedPersonId
  const historicalPersonId = messageDetails.searchPersonId
  const contributorId = messageDetails.contributorId

  const detectedPersonSummary = queueCachesState.personsCache[detectedPersonId]
  const historicalPersonSummary = queueCachesState.personsCache[historicalPersonId]
  const detectedPersonLabels = queueCachesState.labelsCache[detectedPersonId]
  const userSummary = queueCachesState.usersCache[contributorId]

  useEffect(() => {
    queueCachesActions.fetchPersonSummary(detectedPersonId)
    // Not fetched for V2: the curated record is what the matcher actually compared, so showing that
    // person's live tree profile instead would misrepresent the comparison (BR-38).
    if (!isV2) {
      queueCachesActions.fetchPersonSummary(historicalPersonId)
    }
    queueCachesActions.fetchPersonLabels(detectedPersonId)
    queueCachesActions.fetchUserSummary(contributorId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (
    isNullOrUndefined(detectedPersonSummary) ||
    (!isV2 && isNullOrUndefined(historicalPersonSummary)) ||
    isNullOrUndefined(detectedPersonLabels) ||
    isNullOrUndefined(userSummary)
  ) {
    return null
  }

  // One shape for the historical column, whichever side it came from, so the rows below stay uniform.
  const historical = isV2
    ? {
        id: messageDetails.searchPersonId,
        name: messageDetails.name,
        birthPlace: messageDetails.birthPlace,
        birthDate: messageDetails.birthDate,
        deathPlace: messageDetails.deathPlace,
        deathDate: messageDetails.deathDate,
      }
    : {
        id: historicalPersonSummary.id,
        name: historicalPersonSummary?.summary?.name,
        birthPlace: historicalPersonSummary?.birth?.value?.place?.original,
        birthDate: historicalPersonSummary?.birth?.value?.date?.original,
        deathPlace: historicalPersonSummary?.death?.value?.place?.original,
        deathDate: historicalPersonSummary?.death?.value?.date?.original,
      }

  const rows = []
  rows.push({
    label: t('pid', 'PID'),
    detectedPerson: detectedPersonSummary.id ? (
      <>
        <PersonLink personId={detectedPersonSummary.id} forward={false} newTab />
        <TreePersonLink personId={detectedPersonSummary.id} treeIcon newTab />
      </>
    ) : null,
    historicalPerson: historical.id ? (
      <>
        <PersonLink personId={historical.id} forward={false} newTab />{' '}
        <TreePersonLink personId={historical.id} treeIcon newTab />
      </>
    ) : null,
  })

  rows.push({
    label: t('name', 'Name'),
    detectedPerson: detectedPersonSummary?.summary?.name,
    historicalPerson: historical.name,
  })
  rows.push({
    label: t('birthPlace', 'Birth Place'),
    detectedPerson: detectedPersonSummary?.birth?.value?.place?.original,
    historicalPerson: historical.birthPlace,
  })
  rows.push({
    label: t('birthDate', 'Birth Date'),
    detectedPerson: detectedPersonSummary?.birth?.value?.date?.original,
    historicalPerson: historical.birthDate,
  })
  rows.push({
    label: t('deathPlace', 'Death Place'),
    detectedPerson: detectedPersonSummary?.death?.value?.place?.original,
    historicalPerson: historical.deathPlace,
  })
  rows.push({
    label: t('deathDate', 'Death Date'),
    detectedPerson: detectedPersonSummary?.death?.value?.date?.original,
    historicalPerson: historical.deathDate,
  })
  // V2 only: these five are carried in the payload and were rendered nowhere. The old queue's message
  // does not contain them, so the rows would be blank there (BR-42).
  if (isV2) {
    rows.push({
      label: t('parent1', 'Parent 1'),
      detectedPerson: '',
      historicalPerson: messageDetails.parent1Name,
    })
    rows.push({
      label: t('parent2', 'Parent 2'),
      detectedPerson: '',
      historicalPerson: messageDetails.parent2Name,
    })
    rows.push({
      label: t('spouse', 'Spouse'),
      detectedPerson: '',
      historicalPerson: messageDetails.spouseName,
    })
    rows.push({
      label: t('gender', 'Gender'),
      // The tree says MALE and the curated record says Male. Shown as they are, a reviewer reads a
      // difference where there is none, which is the opposite of what a comparison is for.
      detectedPerson: titleCase(detectedPersonSummary?.summary?.gender),
      historicalPerson: titleCase(messageDetails.gender),
    })
    // Which curated name actually produced the match. With several records per person -- "Abe" beside
    // "Abraham" -- this is the only thing telling a reviewer which one hit.
    rows.push({
      label: t('queues.matchedName', 'Matched Name'),
      detectedPerson: '',
      historicalPerson: messageDetails.matchedName,
    })
  }
  // Emitted by the worker, stored and indexed by dmqueues, relayed by DMConsole Server -- and until now
  // rendered nowhere, so a reviewer could not tell a near-certain match from a weak one (BR-39).
  if (!isNullOrUndefined(messageDetails.matchLevel)) {
    rows.push({
      label: t('queues.matchLevel', 'Match Level'),
      detectedPerson: messageDetails.matchLevel,
      historicalPerson: null,
    })
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={6}>
        <DataBlock
          heading={t('detectedPerson', 'Detected Person')}
          data={
            <>
              <PersonLink personId={detectedPersonId} personName={detectedPersonSummary?.summary?.name} showId newTab />
              <TreePersonLink personId={detectedPersonId} treeIcon newTab />
            </>
          }
        />
      </Cell>
      <Cell columns={6}>
        <DataBlock
          heading={t('contributor', 'Contributor')}
          data={<UserLink userId={contributorId} userName={userSummary?.contactName} showId newTab />}
        />
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <DmcGrid columns={[2, 5, 5]}>
          <DataBlock heading="" data="" />
          <DataBlock heading={t('queues.historicalPerson', 'Historical Person')} data="" />
          <DataBlock heading={t('detectedPerson', 'Detected Person')} data="" />
          {rows?.map((row) => {
            return (
              <React.Fragment key={`${row.label}${row.historicalPerson}${row.detectedPerson}`}>
                <DataBlock heading={row.label} data="" />
                <DmcText text={row.historicalPerson} />
                <DmcText text={row.detectedPerson} />
              </React.Fragment>
            )
          })}
        </DmcGrid>
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <DataBlock
          heading={t('queues.historicalType', 'Historical Type')}
          data={messageDetails?.historicalType ?? ''}
        />
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        {/* ?? '' because DataBlock requires `data`; an absent optional field would otherwise log a prop-type
            warning on every render. Pre-existing, and true of the old queue too. */}
        <DataBlock heading={t('biography', 'Biography')} data={messageDetails?.biography ?? ''} />
      </Cell>

      <TreeIdCells treeId={detectedPersonSummary?.summary?.treeId} isResearcherTree="hide" />

      {/* Row */}
      <LabelsBlock personLabels={detectedPersonLabels} />
    </Grid>
  )
}
